import { useState, useEffect, useRef } from "react";
import { Container, StyledReactModal } from "./style";
import Button from "components/particles/primary-button";
import { useSessionPrerequisites } from "containers/setting/CMS/session-prerequisite/useHook";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import close from "assets/images/close.svg";
import Imgclose from "assets/images/input-img-close-icon.svg";
import Swal from "sweetalert2";

interface AddSessionPrerequisitePopupProps {
  id: number;
  data?: any;
  refreshData: () => void;
}

export default function AddSessionPrerequisitePopup({
  id,
  data,
  refreshData,
}: AddSessionPrerequisitePopupProps) {
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<string[]>([]);
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { postSessionPrerequisite, putSessionPrerequisite } =
    useSessionPrerequisites();
  const { sessionPrerequisiteModalIsOpen } = useSelector(
    (state: RootState) => state.user
  );

  function closeModal() {
    dispatch(setModalClosed("Session Prerequisite"));
    setDocuments([]);
    setExistingDocuments([]);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  useEffect(() => {
    if (data && id !== 0) {
      setExistingDocuments(data.filename || []);
      setDocuments([]);
    } else {
      setExistingDocuments([]);
      setDocuments([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [data, id]);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);

      if (documents.length + selectedFiles.length > 1) {
        setError("Maximum 1 document allowed");
        return;
      }

      const allowedTypes = ["application/pdf"];

      const validFiles = selectedFiles.filter((file) =>
        allowedTypes.includes(file.type)
      );

      if (validFiles.length !== selectedFiles.length) {
        setError("Only PDF files are allowed");
        return;
      }

      setDocuments((prev) => [...prev, ...validFiles]);
      setError("");
    }
  };

  const handleRemoveFile = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingDocument = (index: number) => {
    setExistingDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (id === 0) {
      if (documents.length === 0) {
        setError("Please upload at least one document");
        return;
      }

      if (documents.length > 1) {
        setError("Maximum 1 document allowed");
        return;
      }
    } else {
      if (documents.length === 0 && existingDocuments.length === 0) {
        setError("Please upload at least one document or keep existing documents");
        return;
      }

      if (documents.length > 1) {
        setError("Maximum 1 document allowed");
        return;
      }
    }

    try {
      if (id === 0) {
        const formData = new FormData();
        documents.forEach((file) => {
          formData.append("documents", file);
        });
        await postSessionPrerequisite(formData);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Session prerequisite created successfully",
        });
      } else {
        if (documents.length > 0) {
          const formData = new FormData();
          documents.forEach((file) => {
            formData.append("documents", file);
          });
          await putSessionPrerequisite(id, formData);
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Session prerequisite updated successfully",
          });
        } else {
          Swal.fire({
            icon: "info",
            title: "Info",
            text: "No changes made to documents",
          });
          closeModal();
          return;
        }
      }

      refreshData();
      closeModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to save session prerequisite";
      setError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMessage,
      });
    }
  };

  return (
    <StyledReactModal
      isOpen={sessionPrerequisiteModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Session Prerequisite Modal"
    >
      <button className="close-button" onClick={closeModal}>
        <img src={close} alt="close" />
      </button>
      <Container onSubmit={handleSubmit}>
        <div className="model-container">
          <div className="popup-title">
            <h1>
              {id === 0 ? "Add Session Prerequisite" : "Update Session Prerequisite"}
            </h1>
          </div>

          <div className="add-ingredient-container">
            <div className="file-upload-section">
              <p className="file-info">
                Only PDF files are accepted
              </p>

              {/* Existing documents (for edit mode) */}
              {id !== 0 && existingDocuments.length > 0 && documents.length === 0 && (
                <div className="existing-documents">
                  <h4>Uploaded Document:</h4>
                  {existingDocuments.map((link: string, index: number) => (
                    <div key={index} className="document-item">
                      <span
                        className="document-name"
                      >
                        {link}
                      </span>
                      <button
                        type="button"
                        className="remove-doc-btn"
                        onClick={() => handleRemoveExistingDocument(index)}
                      >
                        <img src={Imgclose} alt="Remove" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* New documents */}
              {documents.length > 0 && (
                <div className="new-documents">
                  <h4>New Documents:</h4>
                  {documents.map((file, index) => (
                    <div key={index} className="document-item">
                      <span className="document-name">{file.name}</span>
                      <button
                        type="button"
                        className="remove-doc-btn"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <img src={Imgclose} alt="Remove" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              <button
                type="button"
                className="file-upload-button"
                onClick={handleFileClick}
                disabled={documents.length + existingDocuments.length >= 2}
              >
                {documents.length + existingDocuments.length >= 2
                  ? "Maximum 2 files reached"
                  : "Upload PDF"}
              </button>

              {error && <div className="error-message">{error}</div>}
            </div>
          </div>

          <div className="add-button">
            <Button
              title={id === 0 ? "Save" : "Update"}
              width="100%"
              backgroundcolor="var(--primary)"
              fill="true"
              type="submit"
            />
          </div>
        </div>
      </Container>
    </StyledReactModal>
  );
}

